import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@hub/components/ui/table";
import { apiFetch } from "@hub/ui/api";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";

interface OrgRow {
  slug: string;
  name: string;
  status: string;
  channel: string;
  pinnedVersion: string | null;
  instanceCount: number;
}

export function OrganizationsPage() {
  const [rows, setRows] = useState<OrgRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ data: OrgRow[] }>("/api/orgs")
      .then((r) => setRows(r.data))
      .catch((e: Error) => setError(e.message));
  }, []);

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Organizations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every company on Helm, the release channel it follows, and how many boxes it has.
          </p>
        </div>
        <Button asChild>
          <a href="/orgs/new">
            <Plus /> New organization
          </a>
        </Button>
      </div>

      {error ? (
        <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Pin</TableHead>
              <TableHead>Boxes</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {error ? "Could not load." : "Loading…"}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No organizations yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((o) => (
                <TableRow key={o.slug}>
                  <TableCell>
                    <a
                      href={`/orgs/${encodeURIComponent(o.slug)}`}
                      className="font-medium hover:underline"
                    >
                      {o.name}
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{o.slug}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{o.channel === "stable" ? "Stable" : "Early"}</Badge>
                  </TableCell>
                  <TableCell>{o.pinnedVersion ?? "—"}</TableCell>
                  <TableCell>{o.instanceCount}</TableCell>
                  <TableCell>
                    <Badge variant={o.status === "active" ? "success" : "warning"}>
                      {o.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
